import { useState } from 'react';
import { api } from '../api';
import { LeadScoreBadge } from '../components/LeadScoreBadge';
import type { CallFilterState, CallsResponse } from '../types';
import { formatDateTime, formatDuration, formatNumber } from '../format';
import { OutcomeTag, rawStatusDetail } from '../components/OutcomeTag';
import { ContactJourneyDrawer } from '../components/ContactJourneyDrawer';

interface Props {
  data: CallsResponse | null;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  filters: CallFilterState;
}

type RecordingState = { status: 'checking' } | { status: 'ready' } | { status: 'unavailable'; reason: string };

export function Calls({ data, loading, page, pageSize, onPageChange, onPageSizeChange, filters }: Props) {
  const [recordings, setRecordings] = useState<Record<string, RecordingState>>({});
  const [journeyContactId, setJourneyContactId] = useState<string | null>(null);

  /**
   * An <audio> element cannot report why a source failed, so ask first and
   * render the reason. Most "broken" recordings are calls HighLevel never
   * recorded — unanswered ones, or a sub-account without recording enabled.
   */
  async function play(callId: string) {
    setRecordings((current) => ({ ...current, [callId]: { status: 'checking' } }));
    try {
      const result = await api.checkRecording(callId);
      setRecordings((current) => ({
        ...current,
        [callId]: result.available
          ? { status: 'ready' }
          : { status: 'unavailable', reason: result.reason ?? 'No recording stored for this call.' },
      }));
    } catch (error) {
      setRecordings((current) => ({
        ...current,
        [callId]: { status: 'unavailable', reason: error instanceof Error ? error.message : String(error) },
      }));
    }
  }
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <div className={loading ? 'stack reloading' : 'stack'}>
      <div className="card">
        <div className="card-head">
          <h2>Call log</h2>
          <div className="row">
            <span className="small muted">{data ? `${formatNumber(data.total)} calls` : '—'}</span>
            <a className="btn btn-sm" href={api.callsCsvUrl(filters)} download>
              Export CSV
            </a>
          </div>
        </div>
        <p className="card-sub">
          Contact names link straight to the contact record in HighLevel. Recordings stream through this
          server, so no HighLevel token ever reaches the browser.
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Sub-account</th>
                <th>Contact</th>
                <th title="Out of 10, from the contact's DSCR form answers. Hover a rating for the breakdown.">
                  Lead rating
                </th>
                <th>Made by</th>
                <th>Direction</th>
                <th>Outcome</th>
                <th className="num">Duration</th>
                <th>Recording</th>
              </tr>
            </thead>
            <tbody>
              {(data?.calls ?? []).map((call) => (
                <tr key={call.id}>
                  <td className="nowrap">{formatDateTime(call.dateAdded)}</td>
                  <td>{call.locationName ?? call.locationId}</td>
                  <td>
                    {call.contactId ? (
                      <button
                        type="button"
                        className="link-button cell-strong"
                        onClick={() => setJourneyContactId(call.contactId)}
                        title="Open this contact's journey"
                      >
                        {call.contactName ?? call.contactPhone ?? 'Open contact'}
                      </button>
                    ) : (
                      <span className="cell-strong">{call.contactName ?? call.contactPhone ?? '—'}</span>
                    )}
                    <div className="cell-sub">
                      {call.contactPhone && call.contactName ? `${call.contactPhone} · ` : ''}
                      {call.contactUrl ? (
                        <a href={call.contactUrl} target="_blank" rel="noreferrer">
                          HighLevel
                        </a>
                      ) : null}
                    </div>
                  </td>
                  <td className="nowrap">
                    <LeadScoreBadge lead={call.leadScore} compact />
                  </td>
                  <td>
                    {call.userName ?? (call.userId ? call.userId : <span className="muted">Automated</span>)}
                    {call.source && !call.userId ? <div className="cell-sub">{call.source}</div> : null}
                  </td>
                  <td className="nowrap">{call.direction ?? '—'}</td>
                  <td>
                    <OutcomeTag outcome={call.outcome} detail={call.callStatus} />
                    {rawStatusDetail(call.outcome, call.callStatus) ? (
                      <div className="cell-sub">{rawStatusDetail(call.outcome, call.callStatus)}</div>
                    ) : null}
                  </td>
                  <td className="num">{formatDuration(call.durationSeconds)}</td>
                  <td>
                    {(() => {
                      const state = recordings[call.id];
                      if (!state) {
                        return (
                          <button type="button" className="btn btn-sm" onClick={() => play(call.id)}>
                            ▶ Play
                          </button>
                        );
                      }
                      if (state.status === 'checking') return <span className="small muted">Loading…</span>;
                      if (state.status === 'unavailable') {
                        return (
                          <span className="small muted" title={state.reason}>
                            No recording
                          </span>
                        );
                      }
                      return (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <audio
                          controls
                          autoPlay
                          preload="none"
                          src={api.recordingUrl(call.id)}
                          style={{ height: 30, maxWidth: 220 }}
                        />
                      );
                    })()}
                  </td>
                </tr>
              ))}
              {data && data.calls.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty">
                    No calls match these filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <div className="row">
            <label htmlFor="page-size" className="muted">
              Rows
            </label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {journeyContactId ? (
        <ContactJourneyDrawer contactId={journeyContactId} onClose={() => setJourneyContactId(null)} />
      ) : null}
    </div>
  );
}
